import { useQueryClient } from '@tanstack/react-query';
import { Graphback } from '../../graphql';
import { useStorageKeys } from '../../storage';
import { useSetCurrentWorkspace } from './useSetCurrentWorkspace';
import { useGrippContext } from '../../grippContext';
import { AUTHENTICATION_QUERY_KEY } from '../constants';
import { Authentication } from '../domain';
import { useUnsyncedWorkGuard } from '../../offline/useUnsyncedWorkGuard';

export const useLogout = () => {
  const { secureStorageHandler, storageHandler, offlineCache, logger } =
    useGrippContext();
  const storageKeys = useStorageKeys();
  const queryClient = useQueryClient();
  const { mutateAsync: setCurrentWorkspace } = useSetCurrentWorkspace();
  const confirmLeave = useUnsyncedWorkGuard();

  /** Resolves false, with the session untouched, when the user chose to keep their unsynced work. */
  return async (): Promise<boolean> => {
    if (!(await confirmLeave())) return false;
    // Read before the token goes: afterwards there is no account to clear for.
    const accountId = queryClient.getQueryData<Authentication>(
      AUTHENTICATION_QUERY_KEY
    )?.jwt?.sub;
    await secureStorageHandler.deleteItem(storageKeys.accessToken);
    // Signing out while impersonating a user must also drop the saved
    // original token — useIsInImpersonateUser treats its presence as "still
    // impersonating", so the next signed-in account would wrongly show an
    // active impersonation until a fresh code-login clears it.
    await secureStorageHandler.deleteItem(storageKeys.oldAccessToken);
    await storageHandler.clear();
    // In-flight responses must not land after the next sign-in.
    await queryClient.cancelQueries();
    // Reset without refetching: a refetch would go out tokenless, and the API
    // refuses the mounted screens' workspace-scoped queries as anonymous.
    // Writing the signed-out state unmounts them instead.
    Graphback.dispose();
    queryClient
      .getQueryCache()
      .findAll()
      .forEach((query) => query.reset());
    queryClient.setQueryData<Authentication>(AUTHENTICATION_QUERY_KEY, {
      isLoading: false,
      isAuthenticated: false,
    });
    await setCurrentWorkspace(undefined);
    // A failed wipe must not leave the user signed in.
    if (offlineCache && accountId) {
      try {
        await offlineCache.clear(accountId);
      } catch (error) {
        logger?.error(error, 'Failed to clear the offline cache on logout');
      }
    }
    return true;
  };
};
